/**
 * What the company has promised to pay again and again.
 *
 * Rent, subscriptions, AMCs, insurance. The point of the screen is that one
 * approval covers the term, so afterwards nobody re-approves the same rent
 * twelve times — but nothing is paid by a clock either. "Raise due payments"
 * is a button a person presses, and pressing it twice raises nothing twice.
 *
 * Money paid ahead of the service is not this month's cost: it sits in the
 * prepaid asset and a Book Keeper moves one month of it to the expense head at
 * a time, which is the Prepaid card at the bottom.
 */

import { Fragment, useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Field, Loading, Note, Pill, Stat } from '../components/ui'
import Attachments from '../components/Attachments'
import { Pager, usePaged } from '../components/Pager'
import { SidePanel, Tabs, matches, rowProps, type TabSpec } from '../components/SidePanel'
import SearchSelect from '../components/SearchSelect'

interface Option {
  id: string
  name: string
}

interface FormOptions {
  departments: Option[]
  locations: Option[]
  cost_centers: Option[]
  categories: Option[]
  vendors: Option[]
  my_department_id: string | null
  my_location_id: string | null
}

//: Only what choosing one needs. The bill's own screen owns the rest.
interface PostedBill {
  id: string
  supplier_invoice_no: string
  party_id: string
  invoice_date: string
  status: string
  total_paise: number
}

interface Bill {
  id: string
  reference: string
  supplier_invoice_no: string
  invoice_date: string
  due_date: string | null
  status: string
  taxable_paise: number
  gst_paise: number
  total_paise: number
  tds_paise: number
  net_payable_paise: number
  outstanding_paise: number
}

interface Occurrence {
  id: string
  sequence: number
  period_start: string
  period_end: string
  due_on: string
  expected_total_paise: number
  expected_gst_paise: number
  //: Expected to be withheld from this payment. Zero where none is.
  expected_tds_paise: number
  state: string
  //: upcoming / due / overdue / partially_paid / paid / cancelled.
  payment_status: string
  //: What this payment is for, on a contract whose payments are not alike.
  note: string | null
  skip_reason: string | null
  payment_request_id: string | null
  payment_request_reference: string | null
  payment_request_status: string | null
  bill: Bill | null
  document_count: number
}

interface Revision {
  id: string
  effective_from: string
  amount_total_paise: number
  tds_expected_paise: number
  new_ends_on: string | null
  reason: string
  status: string
  approval_state: string
}

//: The bill is not entered here. It is a posted vendor bill with lines, tax
//: and a journal entry behind it, raised in Bills where that work belongs.
//: What happens here is saying *which* payment of *which* contract it is the
//: bill for. Recording the invoice number a second time on the schedule would
//: give the company two of them, and the one a GST return is filed from would
//: not be this one.
function BillCell({
  commitment,
  occurrence,
  onDone,
}: {
  commitment: Commitment
  occurrence: Occurrence
  onDone: () => void
}) {
  const { can } = useAuth()
  const [picking, setPicking] = useState(false)
  const [chosen, setChosen] = useState('')

  const candidates = useQuery({
    queryKey: ['invoices'],
    queryFn: () => api.get<PostedBill[]>('/procurement/invoices'),
    enabled: picking,
  })
  const link = useMutation({
    mutationFn: () =>
      api.post<Commitment>(`/commitments/occurrences/${occurrence.id}/bill`, {
        vendor_invoice_id: chosen,
      }),
    onSuccess: () => {
      setPicking(false)
      setChosen('')
      onDone()
    },
  })

  if (occurrence.bill) {
    const bill = occurrence.bill
    return (
      <div>
        <span className="mono">{bill.supplier_invoice_no}</span>
        <div className="faint">
          {formatInr(bill.total_paise)}
          {bill.tds_paise > 0 && <> less {formatInr(bill.tds_paise)} TDS</>}
        </div>
        <div className="faint">{formatInr(bill.net_payable_paise)} to the vendor</div>
        {occurrence.document_count > 0 && (
          <div className="faint">
            {occurrence.document_count} file{occurrence.document_count === 1 ? '' : 's'}
          </div>
        )}
      </div>
    )
  }

  const raised = Boolean(occurrence.payment_request_reference)
  if (
    !can('ap.invoice.view') ||
    occurrence.state === 'skipped' ||
    occurrence.state === 'cancelled'
  ) {
    return <span className="faint">—</span>
  }
  if (!picking) {
    return (
      <div>
        <button className="small" disabled={!raised} onClick={() => setPicking(true)}>
          Link a bill
        </button>
        {!raised && (
          <div className="faint">
            raise the payment first — the bill is linked to the request that pays it
          </div>
        )}
      </div>
    )
  }

  const posted = (candidates.data ?? []).filter(
    (row) => row.party_id === commitment.party_id && row.status === 'posted',
  )
  return (
    <div>
      <ErrorNote error={link.error} />
      <select value={chosen} onChange={(e) => setChosen(e.target.value)}>
        <option value="">Which bill…</option>
        {posted.map((row) => (
          <option key={row.id} value={row.id}>
            {row.supplier_invoice_no} — {formatInr(row.total_paise)} ({row.invoice_date})
          </option>
        ))}
      </select>
      {candidates.isSuccess && posted.length === 0 && (
        <div className="faint">
          no posted bill from {commitment.party_name} yet. Record it in Bills first.
        </div>
      )}
      <div>
        <button
          className="primary small"
          disabled={!chosen || link.isPending}
          onClick={() => link.mutate()}
        >
          Link
        </button>{' '}
        <button className="small" onClick={() => setPicking(false)}>
          Cancel
        </button>
      </div>
    </div>
  )
}

//: A milestone contract's payments are entered rather than generated, so
//: this is the only schedule anybody types into. The cap is the server's --
//: the form only says what is left, because a browser that decides what fits
//: is a second opinion about the same rule.
function Milestones({ commitment, onDone }: { commitment: Commitment; onDone: () => void }) {
  const [form, setForm] = useState({ due_on: '', amount: '', gst: '', tds: '', note: '' })
  const add = useMutation({
    mutationFn: () =>
      api.post<Commitment>(`/commitments/${commitment.id}/milestones`, {
        due_on: form.due_on,
        taxable_paise: toPaise(form.amount),
        gst_paise: toPaise(form.gst),
        tds_paise: toPaise(form.tds),
        note: form.note || null,
      }),
    onSuccess: () => {
      setForm({ due_on: '', amount: '', gst: '', tds: '', note: '' })
      onDone()
    },
  })

  return (
    <Card
      title="Add a payment"
      hint={`${formatInr(commitment.unallocated_paise)} of the contract is not yet scheduled`}
    >
      <ErrorNote error={add.error} />
      <div className="row">
        <Field label="Due on">
          <input
            type="date"
            value={form.due_on}
            onChange={(e) => setForm({ ...form, due_on: e.target.value })}
          />
        </Field>
        <Field label="Amount (₹)">
          <input
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
          />
        </Field>
        <Field label="GST (₹)" caption="if the milestone is billed with tax">
          <input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} />
        </Field>
        <Field label="TDS (₹)" caption="withheld from the amount, before GST">
          <input value={form.tds} onChange={(e) => setForm({ ...form, tds: e.target.value })} />
        </Field>
        <Field label="What it is for" caption="initial payment, on completion…">
          <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </Field>
        <div className="shrink">
          <button
            className="primary"
            disabled={add.isPending || !form.due_on || !form.amount}
            onClick={() => add.mutate()}
          >
            Add
          </button>
        </div>
      </div>
      <p className="muted" style={{ marginBottom: 0 }}>
        Payments on a contract like this are whatever the agreement says, on whatever dates it says.
        They may not add up to more than the contract is worth.
      </p>
    </Card>
  )
}

interface ScheduleLine {
  period_label: string
  period_end: string
  amount_paise: number
  status: string
}

interface Schedule {
  id: string
  service_start: string
  service_end: string
  total_paise: number
  recognised_paise: number
  status: string
  lines: ScheduleLine[]
}

interface Quotations {
  required: number
  counted: number
  why: string | null
  waived: boolean
  met: boolean
}

interface Commitment {
  id: string
  reference: string
  kind: string
  title: string
  party_id: string
  party_name: string
  owner_user_id: string
  owner_name: string | null
  department_id: string
  location_id: string
  cost_center_id: string | null
  category_id: string
  starts_on: string
  ends_on: string | null
  frequency: string
  billing: string
  due_day: number | null
  amount_taxable_paise: number
  amount_gst_paise: number
  amount_total_paise: number
  monthly_equivalent_paise: number
  approval_value_paise: number
  tds_expected: boolean
  //: Per period: expected to be withheld, and what then reaches the vendor.
  tds_expected_paise: number
  net_payable_paise: number
  //: The same two over the span the approval covers.
  term_tds_paise: number
  term_payable_paise: number
  treatment: string
  occurrence_approval: string
  auto_renew: boolean
  notice_days: number
  contract_reference: string | null
  contract_value_paise: number
  duration_days: number | null
  days_remaining: number | null
  notice_by: string | null
  notice_passed: boolean
  expiring_soon: boolean
  expired: boolean
  scheduled_paise: number
  paid_paise: number
  unallocated_paise: number
  renewal_alert_days: number
  renewal_price_increase_bp_trigger: number | null
  escalation_bp: number | null
  escalation_every_months: number | null
  mandate_reference: string | null
  quotation_waiver_reason: string | null
  status: string
  approval_state: string
  //: When sent back: what needs correcting, by whom and when.
  return_note: string | null
  returned_by_name: string | null
  returned_at: string | null
  suspend_reason: string | null
  terminated_on: string | null
  termination_reason: string | null
  supersedes_id: string | null
  supersedes_reference: string | null
  renewed_by_reference: string | null
  quotations: Quotations
  occurrences: Occurrence[]
  revisions: Revision[]
  prepaid_schedules: Schedule[]
}

interface TdsSuggestion {
  applies: boolean
  tds_paise: number
  rate_bp: number
  head: string | null
  section: string | null
  reason: string
}

interface VendorTds {
  party_id: string
  party_name: string
  tds_section: string | null
  tds_head: string | null
  commitments: number
  basic_paise: number
  gst_paise: number
  tds_expected_paise: number
  payable_paise: number
  tds_withheld_paise: number
  shortfall_paise: number
}

interface VendorTdsReport {
  fy_code: string
  rows: VendorTds[]
}

interface Due {
  commitment_id: string
  reference: string
  title: string
  party_name: string
  occurrence_id: string
  period_start: string
  period_end: string
  due_on: string
  amount_paise: number
  state: string
  approval: string
  payment_request_reference: string | null
}

interface PrepaidDue {
  schedule_id: string
  line_id: string
  commitment_reference: string | null
  title: string | null
  party_name: string | null
  period_label: string
  period_end: string
  amount_paise: number
}

interface PrepaidSummary {
  held_paise: number
  schedules: number
  due_now: number
  due_now_paise: number
}

const KINDS = [
  ['rent', 'Rent'],
  ['lease', 'Lease'],
  ['subscription', 'Subscription'],
  ['amc', 'AMC / service contract'],
  ['insurance', 'Insurance'],
  ['maintenance', 'Maintenance'],
  ['internet_utility', 'Internet & utilities'],
  ['service_contract', 'Professional services'],
  ['other', 'Other'],
] as const

const FREQUENCIES = [
  ['monthly', 'Every month'],
  ['quarterly', 'Every quarter'],
  ['half_yearly', 'Every six months'],
  ['yearly', 'Every year'],
  ['upfront_once', 'Once, upfront, for the whole term'],
] as const

const TREATMENTS = [
  ['expense_when_due', 'Cost when it falls due'],
  ['prepaid_amortised', 'Paid ahead — spread over the months it covers'],
  ['deposit', 'A deposit — ours, held by somebody else'],
] as const

//: Said once, under the pill, because "overdue" on a row somebody has
//: already raised and approved reads as an accusation until you know it only
//: means the date has passed.
const STANDS_NOTE: Record<string, string> = {
  upcoming: 'not expected yet',
  due: 'falling due now',
  overdue: 'the date has passed and the money has not gone',
  partially_paid: 'part of it has settled',
  paid: 'paid and matched',
  cancelled: 'not being paid',
}

const STATE_NOTE: Record<string, string> = {
  scheduled: 'nothing raised yet',
  requested: 'payment raised',
  part_paid: 'part paid',
  settled: 'paid and matched',
  skipped: 'skipped',
  cancelled: 'cancelled',
}

function toPaise(text: string): number {
  const clean = text.replace(/[^0-9.-]/g, '')
  if (!clean) return 0
  return Math.round(Number(clean) * 100)
}

const today = () => new Date().toISOString().slice(0, 10)

type Tab = 'list' | 'due' | 'tds' | 'prepaid'

/** Subscriptions and rent are the same object; the route decides the filter. */
export default function Commitments({ only }: { only?: string } = {}) {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState<string | null>(null)
  const [drafting, setDrafting] = useState(false)
  const [tab, setTab] = useState<Tab>('list')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['commitments'] })
    void queryClient.invalidateQueries({ queryKey: ['commitments-due'] })
    void queryClient.invalidateQueries({ queryKey: ['prepaid'] })
  }

  const rows = useQuery({
    queryKey: ['commitments', only ?? 'all'],
    queryFn: () => api.get<Commitment[]>(`/commitments${only ? `?kind=${only}` : ''}`),
  })
  const due = useQuery({
    queryKey: ['commitments-due'],
    queryFn: () => api.get<Due[]>('/commitments/payments/upcoming?days=45'),
  })
  const prepaidDue = useQuery({
    queryKey: ['prepaid', 'due'],
    queryFn: () => api.get<PrepaidDue[]>('/commitments/prepaid/due'),
    enabled: can('commit.prepaid.view'),
  })
  const prepaidSummary = useQuery({
    queryKey: ['prepaid', 'summary'],
    queryFn: () => api.get<PrepaidSummary>('/commitments/prepaid/summary'),
    enabled: can('commit.prepaid.view'),
  })
  //: The March question in any month: per vendor, what the contracts expect
  //: to be withheld this year against what the bills actually recorded.
  const tdsByVendor = useQuery({
    queryKey: ['commitments', 'tds-by-vendor'],
    queryFn: () => api.get<VendorTdsReport>('/commitments/tds-by-vendor'),
    enabled: can('commit.commitment.view'),
  })

  const raise = useMutation({
    mutationFn: () =>
      api.post<{ raised: number; references: string[] }>('/commitments/payments/raise-due'),
    onSuccess: refresh,
  })
  const recognise = useMutation({
    mutationFn: () => api.post('/commitments/prepaid/recognise', {}),
    onSuccess: refresh,
  })

  const listed = rows.data ?? []
  const waiting = due.data ?? []
  const prepaidRows = prepaidDue.data ?? []
  const tdsRows = tdsByVendor.data?.rows ?? []
  const shownList = listed.filter(
    (c) =>
      (!status || c.status === status) &&
      matches(search, c.reference, c.title, c.party_name, c.kind, c.contract_reference),
  )
  const shownDue = waiting.filter((d) =>
    matches(search, d.reference, d.title, d.party_name, d.payment_request_reference),
  )
  const shownTds = tdsRows.filter((v) => matches(search, v.party_name, v.tds_section))
  const shownPrepaid = prepaidRows.filter((l) =>
    matches(search, l.title, l.party_name, l.commitment_reference, l.period_label),
  )
  const pagedList = usePaged(shownList, `${status}|${search}`)
  const pagedDue = usePaged(shownDue, search)
  const pagedTds = usePaged(shownTds, search)
  const pagedPrepaid = usePaged(shownPrepaid, search)

  if (rows.isLoading) return <Loading what="Loading commitments" />
  const opened = listed.find((c) => c.id === open)
  const running = listed.filter((c) => c.status === 'active')
  const monthly = running.reduce((sum, c) => sum + c.monthly_equivalent_paise, 0)
  const unraised = waiting.filter((d) => d.state === 'scheduled')
  const noun = only === 'subscription' ? 'Subscriptions' : 'Commitments'
  const statuses = Array.from(new Set(listed.map((c) => c.status)))

  const step = (c: Commitment) => {
    if (c.status === 'draft' || c.status === 'returned')
      return can('commit.commitment.submit')
        ? {
            label: c.status === 'returned' ? 'Correct and resubmit' : 'Ready to submit',
            tone: 'turn',
          }
        : { label: c.status === 'returned' ? 'Sent back' : 'Draft', tone: c.status }
    if (c.status === 'pending_approval')
      return { label: 'Being approved', tone: 'submitted', detail: c.approval_state }
    if (c.status === 'active') {
      if (c.expired) return { label: 'Ended', tone: 'expired' }
      if (c.expiring_soon)
        return {
          label: `Ends in ${c.days_remaining} days`,
          tone: 'advisory',
          detail: c.notice_by && !c.notice_passed ? `notice by ${c.notice_by}` : undefined,
        }
      return { label: 'Running', tone: 'approved' }
    }
    if (c.status === 'suspended')
      return { label: 'Suspended', tone: 'suspended', detail: c.suspend_reason ?? undefined }
    return { label: c.status.replace(/_/g, ' '), tone: c.status }
  }
  const stepCell = (c: Commitment) => {
    const s = step(c)
    return (
      <div className="next-step">
        <span className={`pill ${s.tone}`}>{s.label}</span>
        {'detail' in s && s.detail && <span className="faint">{s.detail}</span>}
      </div>
    )
  }

  const tabs: TabSpec<Tab>[] = [
    { key: 'list', label: noun, count: listed.length },
    { key: 'due', label: 'Falling due in 45 days', count: waiting.length },
    ...(can('commit.commitment.view')
      ? [{ key: 'tds' as Tab, label: 'TDS by vendor', count: tdsRows.length }]
      : []),
    ...(can('commit.prepaid.view')
      ? [
          {
            key: 'prepaid' as Tab,
            label: 'Paid ahead',
            count: prepaidRows.length,
            quietWhenZero: true,
          },
        ]
      : []),
  ]

  return (
    <>
      <ErrorNote error={rows.error ?? due.error ?? raise.error ?? recognise.error} />

      <div className="bank-toolbar">
        {can('commit.commitment.create') && (
          <button className="primary" onClick={() => setDrafting(true)}>
            {only === 'subscription' ? 'New subscription' : 'New commitment'}
          </button>
        )}
        {can('spend.payment_request.create') && (
          <button
            disabled={raise.isPending || unraised.length === 0}
            title={unraised.length === 0 ? 'Every period due has its payment already' : undefined}
            onClick={() => raise.mutate()}
          >
            {raise.isPending ? 'Raising…' : `Raise due payments (${unraised.length})`}
          </button>
        )}
        {can('commit.prepaid.post') && (
          <button
            disabled={recognise.isPending || prepaidRows.length === 0}
            onClick={() => recognise.mutate()}
          >
            {recognise.isPending ? 'Recognising…' : `Recognise prepaid (${prepaidRows.length})`}
          </button>
        )}
      </div>

      {raise.data && (
        <div className="bank-result" style={{ padding: '0 0 12px' }}>
          <Note kind="ok">
            {raise.data.raised === 0
              ? 'Nothing new was due — every period already has its payment.'
              : `Raised ${raise.data.raised}: ${raise.data.references.join(', ')}. A payment ` +
                'that carries its commitment’s approval is already approved; one on its own ' +
                'route is a draft for its owner to submit.'}
          </Note>
          <button className="quiet small" onClick={() => raise.reset()}>
            Dismiss
          </button>
        </div>
      )}

      <div className="grid">
        <Stat
          label={only === 'subscription' ? 'Running subscriptions' : 'Running commitments'}
          value={running.length}
          note={`${listed.length - running.length} not active`}
        />
        <Stat
          label="Committed each month"
          value={formatInr(monthly)}
          note="the run-rate of everything active"
        />
        <Stat
          label="Falling due in 45 days"
          value={formatInr(waiting.reduce((sum, d) => sum + d.amount_paise, 0))}
          note={`${unraised.length} with no payment raised`}
        />
        {can('commit.prepaid.view') && (
          <Stat
            label="Paid ahead, not yet cost"
            value={formatInr(prepaidSummary.data?.held_paise ?? 0)}
            note={
              (prepaidSummary.data?.due_now ?? 0) > 0
                ? `${prepaidSummary.data?.due_now} month(s) to recognise`
                : 'nothing owing recognition'
            }
          />
        )}
      </div>

      <section className="card wide">
        <header className="appr-toolbar">
          <Tabs tabs={tabs} value={tab} onChange={setTab} />
          <div className="appr-filters">
            <input
              type="search"
              placeholder={tab === 'tds' ? 'Vendor, section' : 'Name, vendor, reference'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search"
            />
            {tab === 'list' && (
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                aria-label="Status"
              >
                <option value="">Any status</option>
                {statuses.map((s) => (
                  <option key={s} value={s}>
                    {s.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            )}
          </div>
        </header>

        <div className="body tight">
          {tab === 'list' &&
            (shownList.length === 0 ? (
              <div className="empty">
                {listed.length === 0 ? 'Nothing recorded yet.' : 'Nothing matches that.'}
              </div>
            ) : (
              <table className="appr-table">
                <thead>
                  <tr>
                    <th>What</th>
                    <th>Every</th>
                    <th className="num">Per period</th>
                    <th className="num">Payable</th>
                    <th className="num">A month</th>
                    <th>Runs to</th>
                    <th>Status</th>
                    <th>Next step</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedList.rows.map((c) => (
                    <tr key={c.id} {...rowProps(() => setOpen(c.id), open === c.id)}>
                      <td>
                        <div className="strong">{c.title}</div>
                        <div className="cell-sub">
                          {c.party_name} · {c.kind.replace(/_/g, ' ')} ·{' '}
                          <span className="ref">{c.reference}</span>
                        </div>
                      </td>
                      <td className="muted">{c.frequency.replace(/_/g, ' ')}</td>
                      <td className="num">
                        {formatInr(c.amount_total_paise)}
                        {c.tds_expected_paise > 0 && (
                          <div className="cell-sub">less {formatInr(c.tds_expected_paise)} TDS</div>
                        )}
                      </td>
                      <td className="num">{formatInr(c.net_payable_paise)}</td>
                      <td className="num">
                        <strong>{formatInr(c.monthly_equivalent_paise)}</strong>
                      </td>
                      <td className="muted date">{c.ends_on ?? 'open-ended'}</td>
                      <td>
                        <Pill value={c.status} />
                      </td>
                      <td>{stepCell(c)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}

          {tab === 'due' &&
            (shownDue.length === 0 ? (
              <div className="empty">
                {waiting.length === 0
                  ? 'Nothing falls due in the next 45 days.'
                  : 'Nothing matches that.'}
              </div>
            ) : (
              <table className="appr-table">
                <thead>
                  <tr>
                    <th>Due</th>
                    <th>Commitment</th>
                    <th>Period</th>
                    <th className="num">Amount</th>
                    <th>State</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedDue.rows.map((d) => (
                    <tr
                      key={d.occurrence_id}
                      {...rowProps(() => setOpen(d.commitment_id), false)}
                      title="Open the commitment"
                    >
                      <td className="date">{d.due_on}</td>
                      <td>
                        <div className="strong">{d.title}</div>
                        <div className="cell-sub">
                          {d.party_name} · <span className="ref">{d.reference}</span>
                        </div>
                      </td>
                      <td className="muted date">
                        {d.period_start} → {d.period_end}
                      </td>
                      <td className="num">
                        <strong>{formatInr(d.amount_paise)}</strong>
                      </td>
                      <td>
                        <Pill value={d.state} />
                        {d.payment_request_reference && (
                          <div className="cell-sub mono">{d.payment_request_reference}</div>
                        )}
                        {d.state === 'scheduled' && d.approval === 'own_route' && (
                          <div className="cell-sub">needs its own approval</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}

          {tab === 'tds' && (
            <>
              <div className="faint" style={{ padding: '10px 16px' }}>
                {tdsByVendor.data ? `Financial year ${tdsByVendor.data.fy_code}. ` : ''}What the
                contracts expect to be withheld this year, beside what the bills have actually
                recorded. The expectation is what was agreed when each contract was entered; the
                bill decides what is withheld. A short line is a vendor to look at before March.
              </div>
              <ErrorNote error={tdsByVendor.error} />
              {shownTds.length === 0 ? (
                <div className="empty">
                  {tdsRows.length === 0
                    ? 'No contract carries TDS this year.'
                    : 'No vendor matches that.'}
                </div>
              ) : (
                <table className="appr-table">
                  <thead>
                    <tr>
                      <th>Vendor</th>
                      <th>Section</th>
                      <th className="num">Contracts</th>
                      <th className="num">Basic</th>
                      <th className="num">TDS expected</th>
                      <th className="num">Payable</th>
                      <th className="num">Withheld so far</th>
                      <th className="num">Short</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedTds.rows.map((v) => (
                      <tr key={v.party_id}>
                        <td className="strong">{v.party_name}</td>
                        <td className="mono">
                          {v.tds_section ?? <span className="faint">—</span>}
                        </td>
                        <td className="num">{v.commitments}</td>
                        <td className="num">{formatInr(v.basic_paise)}</td>
                        <td className="num">{formatInr(v.tds_expected_paise)}</td>
                        <td className="num">{formatInr(v.payable_paise)}</td>
                        <td className="num">{formatInr(v.tds_withheld_paise)}</td>
                        <td className="num">
                          {v.shortfall_paise > 0 ? (
                            <span className="pill returned">{formatInr(v.shortfall_paise)}</span>
                          ) : v.shortfall_paise < 0 ? (
                            <span className="muted">over by {formatInr(-v.shortfall_paise)}</span>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}

          {tab === 'prepaid' && (
            <>
              <div className="faint" style={{ padding: '10px 16px' }}>
                A year of software bought in April is not April’s cost. It sits in Prepaid expenses
                and one month of it moves to the expense head at a time. A month cannot close while
                a month of prepaid cost that belongs in it is still in the asset.
              </div>
              <ErrorNote error={prepaidDue.error} />
              {shownPrepaid.length === 0 ? (
                <div className="empty">
                  {prepaidRows.length === 0
                    ? 'Nothing is waiting to be recognised.'
                    : 'Nothing matches that.'}
                </div>
              ) : (
                <table className="appr-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>What</th>
                      <th>Vendor</th>
                      <th className="num">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedPrepaid.rows.map((line) => (
                      <tr key={line.line_id}>
                        <td className="mono">{line.period_label}</td>
                        <td>
                          {line.title ?? '—'}
                          <div className="cell-sub ref">{line.commitment_reference ?? ''}</div>
                        </td>
                        <td>{line.party_name ?? '—'}</td>
                        <td className="num">{formatInr(line.amount_paise)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
        {tab === 'list' && (
          <Pager page={pagedList.page} pages={pagedList.pages} onPage={pagedList.setPage} />
        )}
        {tab === 'due' && (
          <Pager page={pagedDue.page} pages={pagedDue.pages} onPage={pagedDue.setPage} />
        )}
        {tab === 'tds' && (
          <Pager page={pagedTds.page} pages={pagedTds.pages} onPage={pagedTds.setPage} />
        )}
        {tab === 'prepaid' && (
          <Pager
            page={pagedPrepaid.page}
            pages={pagedPrepaid.pages}
            onPage={pagedPrepaid.setPage}
          />
        )}
      </section>

      {opened && (
        <SidePanel
          kicker={opened.kind.replace(/_/g, ' ')}
          title={opened.title}
          sub={
            <>
              {opened.party_name} · <span className="ref">{opened.reference}</span> ·{' '}
              {opened.frequency.replace(/_/g, ' ')}
            </>
          }
          amount={formatInr(opened.amount_total_paise)}
          badge={stepCell(opened)}
          wide
          onClose={() => setOpen(null)}
        >
          <Detail key={opened.id} commitment={opened} onChange={refresh} />
        </SidePanel>
      )}

      {drafting && (
        <SidePanel
          kicker={noun}
          title={only === 'subscription' ? 'A new subscription' : 'A new commitment'}
          sub="saved as a draft; the approver decides on the whole term"
          wide
          onClose={() => setDrafting(false)}
        >
          <DraftForm
            onDone={() => {
              setDrafting(false)
              setTab('list')
              refresh()
            }}
            only={only}
          />
        </SidePanel>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// One commitment
// ---------------------------------------------------------------------------

function Detail({ commitment, onChange }: { commitment: Commitment; onChange: () => void }) {
  const { can } = useAuth()
  const [reason, setReason] = useState('')
  //: Which payment's files are open. One at a time: a schedule with twelve
  //: upload panels showing is not a schedule any more.
  const [papersFor, setPapersFor] = useState<string | null>(null)
  const [endOn, setEndOn] = useState(today())
  const [remainder, setRemainder] = useState('expense')
  const [revising, setRevising] = useState(false)
  //: Correcting a draft or a returned commitment in place. An active one is
  //: never edited in place; that is a revision, through the route.
  const [editing, setEditing] = useState(false)

  const act = useMutation({
    mutationFn: ({ what, body }: { what: string; body?: unknown }) =>
      api.post(`/commitments/${commitment.id}/${what}`, body ?? {}),
    onSuccess: () => {
      setReason('')
      onChange()
    },
  })
  //: Removed rather than cancelled: an unpaid milestone nobody raised is a
  //: plan that turned out to be wrong, and cancelled rows left in the
  //: schedule make "what does this contract still owe?" a question about
  //: statuses. The audit row is what remembers it existed.
  const removeMilestone = useMutation({
    mutationFn: (id: string) =>
      api.post<Commitment>(`/commitments/milestones/${id}/remove`, { reason }),
    onSuccess: () => {
      setReason('')
      onChange()
    },
  })

  const skip = useMutation({
    mutationFn: (occurrenceId: string) =>
      api.post(`/commitments/occurrences/${occurrenceId}/skip`, { reason }),
    onSuccess: () => {
      setReason('')
      onChange()
    },
  })

  const c = commitment
  const running = c.status === 'active' || c.status === 'suspended'

  return (
    <>
      <ErrorNote error={act.error ?? skip.error} />
      {c.status === 'returned' && c.return_note && (
        <Note kind="warn">
          <strong>Sent back for correction</strong>
          {c.returned_by_name && <> by {c.returned_by_name}</>}
          {c.returned_at && <> on {c.returned_at.slice(0, 10)}</>}: “{c.return_note}”. Correct it
          below, then submit it again — it goes through the whole approval route once more.
        </Note>
      )}
      <div className="grid">
        <Stat
          label="Approved on"
          value={formatInr(c.approval_value_paise)}
          note={
            c.term_tds_paise > 0
              ? `the whole term: ${formatInr(c.term_tds_paise)} TDS, ${formatInr(
                  c.term_payable_paise,
                )} to the vendor`
              : 'the whole term'
          }
        />
        <Stat
          label="Per period"
          value={formatInr(c.amount_total_paise)}
          note={
            (c.amount_gst_paise > 0 ? `includes ${formatInr(c.amount_gst_paise)} GST` : 'no GST') +
            (c.tds_expected_paise > 0 ? `; ${formatInr(c.tds_expected_paise)} TDS` : '')
          }
        />
        <Stat
          label="Payable to vendor"
          value={formatInr(c.net_payable_paise)}
          note={
            c.tds_expected_paise > 0
              ? `per period, after ${formatInr(c.tds_expected_paise)} TDS`
              : c.tds_expected
                ? 'TDS is expected; the bill decides how much'
                : 'no TDS expected'
          }
        />
        <Stat
          label="Term"
          value={`${c.starts_on} → ${c.ends_on ?? 'open'}`}
          note={
            c.auto_renew ? `renews by itself; ${c.notice_days} days' notice` : 'no auto-renewal'
          }
        />
        <Stat label="Owner" value={c.owner_name ?? '—'} note="answerable for it" />
      </div>

      {(c.contract_value_paise > 0 || c.contract_reference || c.days_remaining !== null) && (
        <div className="stats">
          {c.contract_reference && (
            <Stat label="Agreement" value={c.contract_reference} note="the vendor's own number" />
          )}
          {c.contract_value_paise > 0 && (
            <Stat
              label="Contract value"
              value={formatInr(c.contract_value_paise)}
              note={
                c.unallocated_paise > 0
                  ? `${formatInr(c.unallocated_paise)} not yet scheduled`
                  : 'fully scheduled'
              }
            />
          )}
          <Stat
            label="Scheduled"
            value={formatInr(c.scheduled_paise)}
            note={`${formatInr(c.paid_paise)} paid`}
          />
          {c.days_remaining !== null && (
            <Stat
              label={c.expired ? 'Expired' : 'Days left'}
              value={c.expired ? String(-c.days_remaining) + ' days ago' : String(c.days_remaining)}
              note={c.duration_days ? `of ${c.duration_days} days` : undefined}
            />
          )}
          {c.notice_by && (
            <Stat
              label="Notice by"
              value={c.notice_by}
              note={
                c.notice_passed ? 'that day has passed' : `${c.notice_days} days before it ends`
              }
            />
          )}
        </div>
      )}

      {c.expiring_soon && !c.expired && (
        <Note kind="warn">
          This contract ends on {c.ends_on} — {c.days_remaining} days away.
          {c.notice_by && !c.notice_passed && <> Notice has to be given by {c.notice_by}.</>}
          {c.notice_passed && <> The date for giving notice has passed.</>}
        </Note>
      )}
      {c.expired && <Note kind="warn">This contract ended on {c.ends_on}.</Note>}

      <table>
        <tbody>
          <tr>
            <th>How each payment is approved</th>
            <td>
              {c.occurrence_approval === 'inherit'
                ? 'It carries this commitment’s approval — for the scheduled amount, to this ' +
                  'vendor, at their current bank account. Anything else needs approving on its own.'
                : 'Each payment goes through its own approval route.'}
            </td>
          </tr>
          <tr>
            <th>How it is accounted</th>
            <td>
              {c.treatment === 'prepaid_amortised'
                ? 'Paid ahead: the payment is an asset, spread over the months it covers.'
                : c.treatment === 'deposit'
                  ? 'A deposit: an asset until it comes back.'
                  : 'Cost in the period it falls due.'}
            </td>
          </tr>
          {c.escalation_bp !== null && c.escalation_every_months !== null && (
            <tr>
              <th>Escalation</th>
              <td>
                {(c.escalation_bp / 100).toFixed(2)}% every {c.escalation_every_months} months,
                counted from the rate in force.
              </td>
            </tr>
          )}
          {c.mandate_reference && (
            <tr>
              <th>Mandate</th>
              <td className="mono">{c.mandate_reference}</td>
            </tr>
          )}
          {c.supersedes_reference && (
            <tr>
              <th>Renews</th>
              <td className="mono">{c.supersedes_reference}</td>
            </tr>
          )}
          {c.renewed_by_reference && (
            <tr>
              <th>Renewed by</th>
              <td className="mono">{c.renewed_by_reference}</td>
            </tr>
          )}
          {c.quotations.required > 0 && (
            <tr>
              <th>Quotations</th>
              <td>
                {c.quotations.counted} of {c.quotations.required} attached
                {c.quotations.why ? ` — ${c.quotations.why}` : ''}
                {c.quotations.waived && ' (waived, with a reason the approvers see)'}
              </td>
            </tr>
          )}
          {c.termination_reason && (
            <tr>
              <th>Ended early</th>
              <td>
                {c.terminated_on} — {c.termination_reason}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {(can('commit.commitment.submit') ||
        can('commit.commitment.approve') ||
        can('commit.commitment.void')) && (
        <div className="row">
          {(c.status === 'draft' || c.status === 'returned') && can('commit.commitment.edit') && (
            <button className="small" onClick={() => setEditing(!editing)}>
              {editing ? 'Close' : c.status === 'returned' ? 'Correct it' : 'Edit'}
            </button>
          )}
          {(c.status === 'draft' || c.status === 'returned') && can('commit.commitment.submit') && (
            <button className="small primary" onClick={() => act.mutate({ what: 'submit' })}>
              {c.status === 'returned' ? 'Submit again for approval' : 'Submit for approval'}
            </button>
          )}
          {c.status === 'active' && can('commit.commitment.approve') && (
            <button
              className="small"
              disabled={reason.trim().length < 3}
              onClick={() => act.mutate({ what: 'suspend', body: { reason } })}
            >
              Suspend
            </button>
          )}
          {c.status === 'suspended' && can('commit.commitment.approve') && (
            <button className="small" onClick={() => act.mutate({ what: 'resume' })}>
              Resume
            </button>
          )}
          {running && can('commit.commitment.void') && (
            <button
              className="small danger"
              disabled={reason.trim().length < 3}
              onClick={() =>
                act.mutate({
                  what: 'terminate',
                  body: { on: endOn, reason, prepaid_remainder: remainder },
                })
              }
            >
              End it early
            </button>
          )}
          {c.ends_on && can('commit.commitment.create') && !c.renewed_by_reference && (
            <button
              className="small"
              onClick={() => act.mutate({ what: 'renew', body: { ends_on: null } })}
            >
              Draft a renewal
            </button>
          )}
          {running && can('commit.commitment.edit') && (
            <button className="small" onClick={() => setRevising(!revising)}>
              {revising ? 'Close' : 'Revise the amount'}
            </button>
          )}
        </div>
      )}

      {(can('commit.commitment.approve') || can('commit.commitment.void')) && running && (
        <div className="row">
          <Field label="Reason" caption="kept on the record, whatever you do next">
            <input value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          {can('commit.commitment.void') && (
            <>
              <Field label="Last day" caption="if ending it early">
                <input type="date" value={endOn} onChange={(e) => setEndOn(e.target.value)} />
              </Field>
              <Field label="Unused prepaid amount">
                <select value={remainder} onChange={(e) => setRemainder(e.target.value)}>
                  <option value="expense">Write it to cost now</option>
                  <option value="refund">The vendor owes it back</option>
                </select>
              </Field>
            </>
          )}
        </div>
      )}

      {revising && (
        <RevisionForm
          commitment={c}
          onDone={() => {
            setRevising(false)
            onChange()
          }}
        />
      )}
      {editing && (
        <DraftForm
          initial={c}
          onDone={() => {
            setEditing(false)
            onChange()
          }}
        />
      )}

      {c.revisions.length > 0 && (
        <>
          <h4>Revisions</h4>
          <table>
            <thead>
              <tr>
                <th>From</th>
                <th className="num">New amount</th>
                <th>New end</th>
                <th>Why</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {c.revisions.map((r) => (
                <tr key={r.id}>
                  <td className="date">{r.effective_from}</td>
                  <td className="num">
                    {formatInr(r.amount_total_paise)}
                    {r.tds_expected_paise > 0 && (
                      <div className="faint">less {formatInr(r.tds_expected_paise)} TDS</div>
                    )}
                  </td>
                  <td className="muted date">{r.new_ends_on ?? '—'}</td>
                  <td>{r.reason}</td>
                  <td>
                    <Pill value={r.status} />
                    {r.status === 'pending_approval' && (
                      <div className="faint">{r.approval_state}</div>
                    )}
                    {r.status === 'draft' && can('commit.commitment.submit') && (
                      <SubmitRevision id={r.id} onDone={onChange} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {c.frequency === 'milestone' &&
        can('commit.commitment.create') &&
        c.status !== 'terminated' && <Milestones commitment={c} onDone={onChange} />}

      <h4>{c.frequency === 'milestone' ? 'Payments' : 'Periods'}</h4>
      {c.frequency === 'milestone' && c.occurrences.length === 0 && (
        <Note kind="info">
          Nothing scheduled yet. This contract's payments are entered one at a time — no frequency
          produces them.
        </Note>
      )}
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>{c.frequency === 'milestone' ? 'For' : 'Period'}</th>
            <th>Due</th>
            <th className="num">Amount</th>
            <th>Stands</th>
            <th>Bill</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {c.occurrences.map((o) => (
            <Fragment key={o.id}>
              <tr>
                <td className="muted">{o.sequence}</td>
                <td className="muted">
                  {c.frequency === 'milestone' ? (
                    (o.note ?? <span className="faint">—</span>)
                  ) : (
                    <span className="date">
                      {o.period_start} → {o.period_end}
                    </span>
                  )}
                </td>
                <td className="date">{o.due_on}</td>
                <td className="num">
                  {formatInr(o.expected_total_paise)}
                  {o.expected_tds_paise > 0 && (
                    <div className="faint">
                      less {formatInr(o.expected_tds_paise)} TDS ={' '}
                      {formatInr(o.expected_total_paise - o.expected_tds_paise)}
                    </div>
                  )}
                </td>
                <td>
                  <Pill value={o.payment_status} />
                  <div className="faint">
                    {o.skip_reason ?? STANDS_NOTE[o.payment_status] ?? ''}
                  </div>
                  <div className="faint">{STATE_NOTE[o.state] ?? ''}</div>
                  {o.payment_request_reference && (
                    <div className="faint mono">{o.payment_request_reference}</div>
                  )}
                </td>
                <td>
                  <BillCell commitment={c} occurrence={o} onDone={onChange} />
                </td>
                <td className="num">
                  {o.state === 'scheduled' && can('commit.commitment.approve') && (
                    <button
                      className="small"
                      disabled={reason.trim().length < 3}
                      onClick={() => skip.mutate(o.id)}
                    >
                      Skip
                    </button>
                  )}{' '}
                  {c.frequency === 'milestone' &&
                    o.state === 'scheduled' &&
                    !o.payment_request_reference &&
                    can('commit.commitment.create') && (
                      <button
                        className="small"
                        disabled={reason.trim().length < 3}
                        title="Say why in the box above first"
                        onClick={() => removeMilestone.mutate(o.id)}
                      >
                        Remove
                      </button>
                    )}{' '}
                  <button
                    className="small"
                    onClick={() => setPapersFor(papersFor === o.id ? null : o.id)}
                  >
                    {o.document_count > 0
                      ? `${o.document_count} file${o.document_count === 1 ? '' : 's'}`
                      : 'Files'}
                  </button>
                </td>
              </tr>
              {/* Under the row it belongs to, not at the top of the card: a
                form that opens somewhere else makes the reader find their
                place again, and on a twelve-month schedule that is a scroll. */}
              {papersFor === o.id && (
                <tr>
                  <td colSpan={7}>
                    <Attachments
                      subjectType="commitment_occurrence"
                      subjectId={o.id}
                      kind="invoice"
                      canAttach={can('commit.commitment.create')}
                      label="Attach the bill, a delivery note, anything else"
                    />
                    {o.bill && (
                      <p className="faint">
                        Files on the bill itself are attached from Bills, against{' '}
                        <span className="mono">{o.bill.reference}</span>.
                      </p>
                    )}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>

      {c.prepaid_schedules.map((s) => (
        <div key={s.id}>
          <h4>
            Spread over months — {formatInr(s.recognised_paise)} of {formatInr(s.total_paise)}{' '}
            recognised
          </h4>
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th>Ends</th>
                <th className="num">Amount</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {s.lines.map((line) => (
                <tr key={line.period_label}>
                  <td className="mono">{line.period_label}</td>
                  <td className="muted date">{line.period_end}</td>
                  <td className="num">{formatInr(line.amount_paise)}</td>
                  <td>
                    <Pill value={line.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </>
  )
}

function SubmitRevision({ id, onDone }: { id: string; onDone: () => void }) {
  const submit = useMutation({
    mutationFn: () => api.post(`/commitments/revisions/${id}/submit`),
    onSuccess: onDone,
  })
  return (
    <button className="small primary" disabled={submit.isPending} onClick={() => submit.mutate()}>
      Submit
    </button>
  )
}

function RevisionForm({ commitment, onDone }: { commitment: Commitment; onDone: () => void }) {
  const [from, setFrom] = useState(today())
  const [amount, setAmount] = useState((commitment.amount_taxable_paise / 100).toFixed(2))
  const [gst, setGst] = useState((commitment.amount_gst_paise / 100).toFixed(2))
  //: Blank means "keep the proportion the contract withheld"; the server
  //: scales it to the new amount. A figure typed here is taken as given.
  const [tds, setTds] = useState('')
  const [payable, setPayable] = useState('')
  const [newEnd, setNewEnd] = useState('')
  const [why, setWhy] = useState('')
  const scaledTds =
    commitment.amount_taxable_paise > 0
      ? Math.round(
          (toPaise(amount) * commitment.tds_expected_paise) / commitment.amount_taxable_paise,
        )
      : 0
  const tdsPaise = tds.trim() ? toPaise(tds) : scaledTds

  const create = useMutation({
    mutationFn: () =>
      api.post(`/commitments/${commitment.id}/revisions`, {
        effective_from: from,
        amount_taxable_paise: toPaise(amount),
        amount_gst_paise: toPaise(gst),
        tds_expected_paise: tds.trim() ? toPaise(tds) : null,
        new_ends_on: newEnd || null,
        reason: why,
      }),
    onSuccess: onDone,
  })

  return (
    <Card title="Revise the amount or the term">
      <ErrorNote error={create.error} />
      <Note>
        A revision takes effect from a date and reprices only the periods from then on. Payments
        already due were approved at the old amount, so nothing behind the date moves.
      </Note>
      <div className="row">
        <Field label="From">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="New basic amount per period">
          <input value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="GST">
          <input value={gst} onChange={(e) => setGst(e.target.value)} />
        </Field>
        <Field
          label="TDS to withhold"
          caption={tds.trim() ? 'as typed' : `same share as now: ${formatInr(scaledTds)}`}
        >
          <input
            value={tds}
            onChange={(e) => setTds(e.target.value)}
            placeholder={(scaledTds / 100).toFixed(2)}
          />
        </Field>
        <Field label="Payable to vendor" caption="basic + GST − TDS; type it and the TDS follows">
          <input
            value={payable}
            placeholder={((toPaise(amount) + toPaise(gst) - tdsPaise) / 100).toFixed(2)}
            onChange={(e) => {
              const typed = e.target.value
              setPayable(typed)
              setTds(((toPaise(amount) + toPaise(gst) - toPaise(typed)) / 100).toFixed(2))
            }}
            onBlur={() => setPayable('')}
          />
        </Field>
        <Field label="New end date" caption="optional">
          <input type="date" value={newEnd} onChange={(e) => setNewEnd(e.target.value)} />
        </Field>
      </div>
      <Field label="Why" caption="the approver reads this">
        <input value={why} onChange={(e) => setWhy(e.target.value)} />
      </Field>
      <button
        className="primary"
        disabled={create.isPending || why.trim().length < 3}
        onClick={() => create.mutate()}
      >
        Propose the revision
      </button>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// A new one
// ---------------------------------------------------------------------------

const rupees = (paise: number) => (paise ? (paise / 100).toFixed(2) : '')

//: The same form drafts a new commitment and corrects a draft or a returned
//: one: with `initial` it starts from that commitment and saves with PUT.
function DraftForm({
  onDone,
  only,
  initial,
}: {
  onDone: () => void
  only?: string
  initial?: Commitment
}) {
  const options = useQuery({
    queryKey: ['spend-form-options'],
    queryFn: () => api.get<FormOptions>('/spend/form-options'),
  })
  const [form, setForm] = useState({
    kind: initial?.kind ?? only ?? 'rent',
    title: initial?.title ?? '',
    party_id: initial?.party_id ?? '',
    department_id: initial?.department_id ?? '',
    location_id: initial?.location_id ?? '',
    category_id: initial?.category_id ?? '',
    cost_center_id: initial?.cost_center_id ?? '',
    starts_on: initial?.starts_on ?? today(),
    ends_on: initial?.ends_on ?? '',
    frequency: initial?.frequency ?? 'monthly',
    billing: initial?.billing ?? 'in_advance',
    due_day: initial ? String(initial.due_day ?? '') : '5',
    amount: initial ? rupees(initial.amount_taxable_paise) : '',
    gst: initial ? rupees(initial.amount_gst_paise) : '',
    tds: initial ? rupees(initial.tds_expected_paise) : '',
    //: Only while being typed; cleared on blur so the field goes back to
    //: showing the computed figure. What is sent is always the TDS.
    payable: '',
    treatment: initial?.treatment ?? 'expense_when_due',
    occurrence_approval: initial?.occurrence_approval ?? 'inherit',
    tds_expected: initial?.tds_expected ?? false,
    auto_renew: initial?.auto_renew ?? false,
    contract_reference: initial?.contract_reference ?? '',
    contract_value: initial ? rupees(initial.contract_value_paise) : '',
    notice_days: String(initial?.notice_days ?? 30),
    renewal_alert_days: String(initial?.renewal_alert_days ?? 30),
    mandate_reference: initial?.mandate_reference ?? '',
  })
  const set = (key: string, value: string | boolean) => setForm((f) => ({ ...f, [key]: value }))

  //: What the vendor's TDS section says to withhold on this amount, worked
  //: out by the same engine the bill will use. It fills the TDS field until
  //: the person types into it; after that their figure stands. The server
  //: still checks whatever is sent.
  //: Starts "touched" when correcting: the figure on the record stands until
  //: the person changes it, rather than being overwritten by the suggestion.
  const [tdsTouched, setTdsTouched] = useState(!!initial)
  const basicPaise = toPaise(form.amount)
  const suggestion = useQuery({
    queryKey: ['tds-suggestion', form.party_id, basicPaise, form.frequency],
    queryFn: () =>
      api.get<TdsSuggestion>(
        `/commitments/tds-suggestion?party_id=${form.party_id}` +
          `&amount_taxable_paise=${basicPaise}&frequency=${form.frequency}`,
      ),
    enabled: !!form.party_id && basicPaise > 0 && form.frequency !== 'milestone',
  })
  useEffect(() => {
    if (!tdsTouched && suggestion.data?.applies) {
      setForm((f) => ({ ...f, tds: (suggestion.data!.tds_paise / 100).toFixed(2) }))
    }
  }, [suggestion.data, tdsTouched])
  const tdsPaise = toPaise(form.tds)
  const payablePaise = basicPaise + toPaise(form.gst) - tdsPaise

  //: One payload, whether drafting or correcting.
  const payload = () => ({
    kind: form.kind,
    title: form.title,
    party_id: form.party_id,
    department_id: form.department_id || options.data?.my_department_id,
    location_id: form.location_id || options.data?.my_location_id,
    category_id: form.category_id,
    cost_center_id: form.cost_center_id || null,
    starts_on: form.starts_on,
    ends_on: form.ends_on || null,
    frequency: form.frequency,
    billing: form.billing,
    due_day: form.due_day ? Number(form.due_day) : null,
    amount_taxable_paise: toPaise(form.amount),
    amount_gst_paise: toPaise(form.gst),
    tds_expected_paise: tdsPaise,
    treatment: form.treatment,
    occurrence_approval: form.occurrence_approval,
    tds_expected: form.tds_expected || tdsPaise > 0,
    auto_renew: form.auto_renew,
    contract_reference: form.contract_reference || null,
    contract_value_paise: toPaise(form.contract_value),
    notice_days: Number(form.notice_days || 30),
    renewal_alert_days: Number(form.renewal_alert_days || 30),
    mandate_reference: form.mandate_reference || null,
  })

  const create = useMutation({
    mutationFn: () =>
      initial
        ? api.put<Commitment>(`/commitments/${initial.id}`, payload())
        : api.post<Commitment>('/commitments', payload()),
    onSuccess: onDone,
  })

  const opts = options.data
  return (
    <div>
      {initial && <h3>Correct {initial.reference}</h3>}
      <ErrorNote error={create.error ?? options.error} />
      <Note>
        The approver decides on the whole term, not one month of it. Once approved, the periods are
        laid out and each one’s payment is raised from here.
      </Note>
      <div className="row">
        <Field label="What kind">
          <select value={form.kind} onChange={(e) => set('kind', e.target.value)}>
            {KINDS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Name it" caption="what people will recognise">
          <input value={form.title} onChange={(e) => set('title', e.target.value)} />
        </Field>
        <Field label="Vendor">
          <select value={form.party_id} onChange={(e) => set('party_id', e.target.value)}>
            <option value="">Choose…</option>
            {(opts?.vendors ?? []).map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="row">
        <Field label="Department" caption="decides who approves it">
          <select
            value={form.department_id || opts?.my_department_id || ''}
            onChange={(e) => set('department_id', e.target.value)}
          >
            <option value="">Choose…</option>
            {(opts?.departments ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Clinic">
          <select
            value={form.location_id || opts?.my_location_id || ''}
            onChange={(e) => set('location_id', e.target.value)}
          >
            <option value="">Choose…</option>
            {(opts?.locations ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="What it is for" caption="decides the expense account">
          <SearchSelect
            aria-label="What it is for"
            value={form.category_id}
            onChange={(v) => set('category_id', v)}
            placeholder="Choose…"
            options={(opts?.categories ?? []).map((c) => ({ value: c.id, label: c.name }))}
          />
        </Field>
      </div>
      <div className="row">
        <Field label="Basic amount per period" caption="before GST; what TDS is taken on">
          <input value={form.amount} onChange={(e) => set('amount', e.target.value)} />
        </Field>
        <Field label="GST" caption="if any">
          <input value={form.gst} onChange={(e) => set('gst', e.target.value)} />
        </Field>
        <Field
          label="TDS to withhold"
          caption={
            tdsTouched
              ? 'as typed'
              : suggestion.data?.applies
                ? `${suggestion.data.section ?? suggestion.data.head} at ${
                    suggestion.data.rate_bp / 100
                  }%`
                : suggestion.data
                  ? 'no TDS section on the vendor; type it if the agreement says so'
                  : 'from the vendor’s section, once the amount is in'
          }
        >
          <input
            value={form.tds}
            //: The statutory reasoning in full, on hover: too long for a
            //: caption, too useful to drop.
            title={suggestion.data?.reason ?? ''}
            onChange={(e) => {
              setTdsTouched(true)
              set('tds', e.target.value)
            }}
          />
        </Field>
        <Field label="Payable to vendor" caption="basic + GST − TDS; type it and the TDS follows">
          <input
            value={form.payable}
            placeholder={(payablePaise / 100).toFixed(2)}
            onChange={(e) => {
              // Entered as a figure of its own, as people have it on the
              // agreement: ₹75,600 of ₹84,000. The TDS is then the gap, so
              // the three never disagree.
              setTdsTouched(true)
              const typed = e.target.value
              const tds = basicPaise + toPaise(form.gst) - toPaise(typed)
              setForm((f) => ({ ...f, payable: typed, tds: (tds / 100).toFixed(2) }))
            }}
            onBlur={() => set('payable', '')}
          />
        </Field>
        <Field label="How often">
          <select value={form.frequency} onChange={(e) => set('frequency', e.target.value)}>
            {FREQUENCIES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Billed">
          <select value={form.billing} onChange={(e) => set('billing', e.target.value)}>
            <option value="in_advance">In advance</option>
            <option value="in_arrears">In arrears</option>
          </select>
        </Field>
        <Field label="Due day" caption="1–28, so it exists every month">
          <input value={form.due_day} onChange={(e) => set('due_day', e.target.value)} />
        </Field>
      </div>
      <div className="row">
        <Field label="Starts">
          <input
            type="date"
            value={form.starts_on}
            onChange={(e) => set('starts_on', e.target.value)}
          />
        </Field>
        <Field label="Ends" caption="leave empty for open-ended">
          <input
            type="date"
            value={form.ends_on}
            onChange={(e) => set('ends_on', e.target.value)}
          />
        </Field>
        <Field label="How it is accounted">
          <select value={form.treatment} onChange={(e) => set('treatment', e.target.value)}>
            {TREATMENTS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Each payment">
          <select
            value={form.occurrence_approval}
            onChange={(e) => set('occurrence_approval', e.target.value)}
          >
            <option value="inherit">Carries this approval</option>
            <option value="own_route">Goes through its own approval</option>
          </select>
        </Field>
      </div>
      <div className="row">
        <Field label="Mandate reference" caption="if the bank debits it automatically">
          <input
            value={form.mandate_reference}
            onChange={(e) => set('mandate_reference', e.target.value)}
          />
        </Field>
        <Field label="Warn me before it ends" caption="days">
          <input
            value={form.renewal_alert_days}
            onChange={(e) => set('renewal_alert_days', e.target.value)}
          />
        </Field>
        <Field label="Agreement number" caption="the vendor's own, as the paper says it">
          <input
            value={form.contract_reference}
            onChange={(e) => set('contract_reference', e.target.value)}
            placeholder="ABC/2026/114"
          />
        </Field>
        <Field
          label="Contract value (₹)"
          caption="what the whole agreement is worth; leave empty if the paper does not say"
        >
          <input
            value={form.contract_value}
            onChange={(e) => set('contract_value', e.target.value)}
          />
        </Field>
        <Field label="Notice to leave" caption="days">
          <input value={form.notice_days} onChange={(e) => set('notice_days', e.target.value)} />
        </Field>
        <label style={{ fontSize: 13 }}>
          <input
            type="checkbox"
            checked={form.tds_expected}
            onChange={(e) => set('tds_expected', e.target.checked)}
          />{' '}
          TDS is expected on this
        </label>
        <label style={{ fontSize: 13 }}>
          <input
            type="checkbox"
            checked={form.auto_renew}
            onChange={(e) => set('auto_renew', e.target.checked)}
          />{' '}
          Renews by itself unless we give notice
        </label>
      </div>
      <button
        className="primary"
        disabled={create.isPending || !form.title || !form.party_id || !form.amount}
        onClick={() => create.mutate()}
      >
        {initial ? 'Save the corrections' : 'Save as a draft'}
      </button>
    </div>
  )
}
